/**
 * Unauthenticated client for the public project portal. Deliberately does NOT go through
 * the MSAL-aware `api/client.ts` — public portal visitors are not signed in. Requests
 * carry no Authorization header; the opaque token in the path is the only credential.
 */
import { API_BASE_PATH, type ApiErrorEnvelope } from '@catalyst-psa/shared';

import { env } from '../../config/env.js';

export class PublicApiError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'PublicApiError';
    this.status = status;
  }
}

async function parsePublicError(response: Response): Promise<PublicApiError> {
  let message = `Request failed with status ${response.status}`;
  try {
    const data = (await response.json()) as ApiErrorEnvelope | undefined;
    if (data?.error?.message) message = data.error.message;
  } catch {
    // Non-JSON body; keep the status-based message.
  }
  return new PublicApiError(message, response.status);
}

async function publicFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  headers.set('Accept', 'application/json');
  if (init?.body !== undefined) headers.set('Content-Type', 'application/json');

  const response = await fetch(`${env.apiBaseUrl}${API_BASE_PATH}${path}`, { ...init, headers });
  if (!response.ok) throw await parsePublicError(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/** Parses a `filename="..."` value out of a Content-Disposition header. */
function parseFileName(disposition: string | null): string | null {
  if (!disposition) return null;
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  return match?.[1]?.trim() ?? null;
}

/** Fetches a binary attachment (e.g. a PDF) plus its server-provided file name. */
async function publicDownload(path: string): Promise<{ blob: Blob; fileName: string | null }> {
  const response = await fetch(`${env.apiBaseUrl}${API_BASE_PATH}${path}`);
  if (!response.ok) throw await parsePublicError(response);
  return {
    blob: await response.blob(),
    fileName: parseFileName(response.headers.get('Content-Disposition')),
  };
}

export const publicApi = {
  get: <T>(path: string): Promise<T> => publicFetch<T>(path),
  post: <T>(path: string, body?: unknown): Promise<T> =>
    publicFetch<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  download: publicDownload,
};

/** Saves a fetched blob to the visitor's downloads via a transient object URL. */
export function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoking immediately can cancel the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
